/**
 * What stands between the transcript and the prompt (D156, split from
 * `session-view.tsx` at the complexity cap): the control row, the port
 * notice (D163), the ending banner, and the countdown — every one a read
 * off the session view, or off the session slice beside it.
 *
 * @packageDocumentation
 */

import type { GameDescription, PassDescription, PortReport, SessionView } from '@orpheus/api-contracts';
import type { MapStylePreference, PlayHost } from '@orpheus/player-host';

import { choosePort, type GameStore, restartSession } from '@orpheus/game-store';
import { CountdownComponent, PaywallNoticeComponent, PortNoticeComponent } from '@orpheus/player-ui';
import { type ReactNode, useEffect, useState } from 'react';

import { describePortReport, portChoiceLabelsFor, portHeadingFor } from './port-copy.js';
import { SessionControls } from './session-controls.js';
import { type CheckoutOutcome, type CheckoutRefusal, type PurchaseArrival, type StoreClient } from './store-client.js';
import { buyLabelFor, CHECKOUT_REFUSED, passLabelFor, passNoteFor, PURCHASE_ARRIVAL } from './store-copy.js';

/** Props for {@link SessionExtras}. */
export interface SessionExtrasProps {
	/** The account control (D157), passed through to the row. */
	readonly account?: ReactNode;
	readonly ambientAudioVolume: number;
	readonly game: GameDescription;
	readonly host: PlayHost;
	readonly isAmbientAudioEnabled: boolean;
	readonly isClockEnabled: boolean;
	readonly isRoomImageryEnabled: boolean;
	/** Whether the last turn was refused at the edge of the demo (D165) — its words are the transcript's; the Buy button is here. */
	readonly isWalled: boolean;
	/** D209: which of the pack's offered map styles the player looks at — passed through to the row. */
	readonly mapStylePreference: MapStylePreference;
	/** Called as the player begins again — or answers a port (D163) — before the host has answered: the shell clears its own echoes. */
	readonly onRestart: () => void;
	/** A newer version the session could not follow silently (D163), while the player has not answered. */
	readonly pendingPort: PortReport | undefined;
	/** What the browser came back from a checkout with (D165). */
	readonly purchaseArrival?: PurchaseArrival | undefined;
	readonly store: GameStore;
	/** The store Worker's client (D165); absent, a paywall has no button. */
	readonly storefront?: StoreClient | undefined;
	/** The pacing driver's mark for the countdown's seconds. */
	readonly view: SessionView;
	readonly turnStartedAt: number | undefined;
}

/**
 * Render the control row, the port notice while one is pending, the ending
 * banner when the story has ended, and the countdown while the view carries
 * one.
 *
 * @param props - {@link SessionExtrasProps}
 * @returns The extras
 */
export const SessionExtras = ({
	account,
	ambientAudioVolume,
	game,
	host,
	isAmbientAudioEnabled,
	isClockEnabled,
	isRoomImageryEnabled,
	isWalled,
	mapStylePreference,
	onRestart,
	pendingPort,
	purchaseArrival,
	store,
	storefront,
	turnStartedAt,
	view,
}: SessionExtrasProps) => {
	// D163: one answer at a time — the buttons hold until the host has spoken.
	const [isAnsweringPort, setIsAnsweringPort] = useState(false);
	// D165: a checkout on its way, and the chrome's one line when it could not start.
	const [isStartingCheckout, setIsStartingCheckout] = useState(false);
	const [checkoutRefusal, setCheckoutRefusal] = useState<CheckoutRefusal | undefined>(undefined);
	// D165c: the pass as the store describes it, asked for once the wall is met on a game the pass covers; nothing otherwise.
	const [pass, setPass] = useState<PassDescription | undefined>(undefined);
	const isPassOffered = storefront !== undefined && isWalled && game.sale?.isInPass === true;

	useEffect(() => {
		if (!isPassOffered || pass !== undefined) {
			return undefined;
		}

		let isDisposed = false;

		// A refusal (no pass on this deployment, the store unreachable) leaves the button off: Buy stands alone, as before.
		void storefront.pass().then((answer) => {
			if (!isDisposed && !('refusal' in answer)) {
				setPass(answer);
			}
		});

		return () => {
			isDisposed = true;
		};
	}, [isPassOffered, pass, storefront]);

	/** Go where the store says, or say why not — the same for a game and for the pass. */
	const goTo = (start: (returnUrl: string) => Promise<CheckoutOutcome>) => {
		setIsStartingCheckout(true);
		setCheckoutRefusal(undefined);
		// The page to come back to is this one, query and all (`?seed=` survives), with the outcome added by the store.
		void start(window.location.href).then((outcome) => {
			if (outcome.kind === 'started') {
				window.location.assign(outcome.url);

				return;
			}

			setCheckoutRefusal(outcome.refusal);
			setIsStartingCheckout(false);
		});
	};

	return (
		<>
			{/* The map and the journal are REFERENCES, not part of the reading
		    surface: they live behind buttons in the control row so neither can
		    push the prompt down the page as the world (or the shelf) grows. */}
			<SessionControls
				account={account}
				ambientAudioVolume={ambientAudioVolume}
				game={game}
				host={host}
				isAmbientAudioEnabled={isAmbientAudioEnabled}
				isClockEnabled={isClockEnabled}
				isRoomImageryEnabled={isRoomImageryEnabled}
				mapStylePreference={mapStylePreference}
				store={store}
				view={view}
			/>
			{/* D163: the platform's question, above the fiction's chrome — a newer
		    version exists and this session could not follow it silently. The
		    input stays enabled: unanswered, the session goes on where it was. */}
			{pendingPort === undefined ? undefined : (
				<PortNoticeComponent
					detail={describePortReport(pendingPort)}
					heading={portHeadingFor(pendingPort)}
					isAnswering={isAnsweringPort}
					onChoose={(choice) => {
						onRestart();
						setIsAnsweringPort(true);
						void store.dispatch(choosePort(choice)).finally(() => {
							setIsAnsweringPort(false);
						});
					}}
					stayLabel={portChoiceLabelsFor(pendingPort).stay}
					updateLabel={portChoiceLabelsFor(pendingPort).update}
				/>
			)}
			{/* D165: the edge of the demo — the one button that is the platform's.
		    The game's own words are the turn's answer, in the transcript. The
		    input stays enabled: the same words go through once the game is
		    bought. */}
			{!isWalled || storefront === undefined ? undefined : (
				<PaywallNoticeComponent
					buyLabel={buyLabelFor(game.sale)}
					isStartingCheckout={isStartingCheckout}
					{...(checkoutRefusal === undefined ? {} : { notice: CHECKOUT_REFUSED[checkoutRefusal] })}
					onBuy={() => {
						goTo(async (returnUrl) => storefront.startCheckout(game.gameId, returnUrl));
					}}
					{...(!isPassOffered || pass === undefined
						? {}
						: {
							onPass: () => {
								goTo(async (returnUrl) => storefront.startPassCheckout(returnUrl));
							},
							passLabel: passLabelFor(pass),
							passNote: passNoteFor(pass),
						})}
				/>
			)}
			{/* D165: back from Stripe — what happened, once, in the chrome's voice. */}
			{purchaseArrival === undefined ? undefined : (
				<p className="session-view__purchase-arrival" role="status">
					{PURCHASE_ARRIVAL[purchaseArrival]}
				</p>
			)}
			{/* R11: a TERMINAL ending closes the world. The input stays enabled —
		    the engine answers every further command with the ending's own
		    refusal, the classic of the genre — but the shell says so plainly
		    and offers the way back in. Open endings add no chrome: their
		    epilogue lines carry it. */}
			{view.isEnded ? (
				<div className="session-view__ending-banner" role="status">
					<span className="session-view__ending-banner-label">THE STORY HAS ENDED</span>
					<button
						className="map-dialog__trigger"
						onClick={() => {
						// The host begins again — a fresh seed, and in client mode the
						// genesis autosave replacing the finished save on its own.
							onRestart();
							void store.dispatch(restartSession());
						}}
						type="button"
					>
						Begin again
					</button>
				</div>
			) : undefined}
			{/* D153: the number exists only while the view carries a countdown —
		    the host's `resolvePacing` is the whole visibility condition. */}
			{view.pacing?.countdown === undefined ? undefined : (
				<CountdownComponent
					autoWaitMs={view.pacing.autoWaitMs}
					label={view.pacing.countdown.label}
					turnStartedAt={turnStartedAt}
					turnsRemaining={view.pacing.countdown.turnsRemaining}
				/>
			)}
		</>
	);
};
